import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ArticleContent } from "@/types/article";
import type { Lang } from "@/i18n/languages";

interface Props {
  article: ArticleContent;
  lang: Lang;
}

export default function ArticleGridCard({ article, lang }: Props) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/${lang}/articles/${article.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl bg-background-50 border border-background-200/50 transition-all hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
    >
      <div className="relative h-52 w-full overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-primary-500 z-10"></div>
        <img
          src={article.image}
          alt={article.h1}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-400"></span>
          {t("nav.articles")}
        </span>
        <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors">
          {article.h1}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">
          {article.metaDescription}
        </p>
        <div className="mt-auto pt-4 flex items-center gap-3 text-xs text-foreground-500 border-t border-background-200/40">
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </span>
            {article.readMinutes} {t("articles.minRead")}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-calendar-line"></i>
            </span>
            {article.updated}
          </span>
        </div>
        <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 whitespace-nowrap">
          {t("articles.readMore")}
          <span className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-0.5">
            <i className="ri-arrow-right-line text-sm"></i>
          </span>
        </span>
      </div>
    </Link>
  );
}